import { create } from "zustand";
import { persist, createJSONStorage, StateStorage } from "zustand/middleware";

export type Env = "telegram" | "browser";

export interface AdminLoginResponseDTO {
    id: number;
    userId: string;
    phoneNumber: string;
    isTempPassword?: boolean | null;
    locationId: number;
    adminBotId: number;
    clientBotId: number;
}

export interface AdminInfoDTO {
    id: number;
    userId: string;
    locationId: number;
    adminBotId: number;
    clientBotId: number;
}

export type AdminUserDTO = AdminLoginResponseDTO | AdminInfoDTO;

type UserStore = {
    env: Env | null;
    admin: AdminUserDTO | null;

    setBrowserAdmin: (dto: AdminLoginResponseDTO) => void;
    setTelegramAdmin: (dto: AdminInfoDTO) => void;
    clear: () => void;

    // удобные геттеры
    locationId: number | null;
    adminBotId: number | null;
    clientBotId: number | null;
    adminUserIdLong: number | null;

    // TTL
    savedAt: number | null;
    isReady: boolean;
    isExpired: () => boolean;
};

const STORAGE_KEY = "b2b-admin-user-store";

// для локального тестирования: 2 минуты
const TTL_MS = 120 * 60 * 1000;
// для прода потом: 24 * 60 * 60 * 1000;

/**
 * Безопасные обёртки над localStorage.
 * В Telegram in-app WebView, Safari Private Mode, MAX мессенджер-браузере
 * вызовы localStorage могут БРОСАТЬ исключения. Без try/catch это ломает
 * zustand persist: onRehydrateStorage не вызывается, isReady остаётся false,
 * приложение зависает на "Загрузка профиля..." навсегда.
 */
function safeGet(name: string): string | null {
    try {
        return typeof localStorage !== "undefined" ? localStorage.getItem(name) : null;
    } catch {
        return null;
    }
}

function safeSet(name: string, value: string): void {
    try {
        if (typeof localStorage !== "undefined") localStorage.setItem(name, value);
    } catch {
        /* приватный режим / ограниченный WebView — просто игнорируем */
    }
}

function safeRemove(name: string): void {
    try {
        if (typeof localStorage !== "undefined") localStorage.removeItem(name);
    } catch {
        /* игнорируем */
    }
}

const ttlStorage: StateStorage = {
    getItem: (name) => {
        const raw = safeGet(name);
        if (!raw) return null;

        try {
            const parsed = JSON.parse(raw);
            const savedAt: number | null = parsed?.state?.savedAt ?? null;

            if (!savedAt) {
                safeRemove(name);
                return null;
            }

            if (Date.now() - savedAt > TTL_MS) {
                safeRemove(name);
                return null;
            }

            return raw;
        } catch {
            safeRemove(name);
            return null;
        }
    },
    setItem: (name, value) => safeSet(name, value),
    removeItem: (name) => safeRemove(name),
};

export const useUserStore = create<UserStore>()(
    persist(
        (set, get) => ({
            env: null,
            admin: null,

            setBrowserAdmin: (dto) =>
                set(() => ({
                    env: "browser",
                    admin: dto,
                    locationId: dto.locationId ?? null,
                    adminBotId: dto.adminBotId ?? null,
                    clientBotId: dto.clientBotId ?? null,
                    adminUserIdLong: dto.userId ? Number(dto.userId) : null,
                    savedAt: Date.now(),
                })),

            setTelegramAdmin: (dto) =>
                set(() => ({
                    env: "telegram",
                    admin: dto,
                    locationId: dto.locationId ?? null,
                    adminBotId: dto.adminBotId ?? null,
                    clientBotId: dto.clientBotId ?? null,
                    adminUserIdLong: dto.userId ? Number(dto.userId) : null,
                    savedAt: Date.now(),
                })),

            clear: () =>
                set(() => ({
                    env: null,
                    admin: null,
                    locationId: null,
                    adminBotId: null,
                    clientBotId: null,
                    adminUserIdLong: null,
                    savedAt: null,
                })),

            locationId: null,
            adminBotId: null,
            clientBotId: null,
            adminUserIdLong: null,

            savedAt: null,
            // ВАЖНО: стартуем сразу как готовы, чтобы UI рендерил форму логина
            // даже если localStorage недоступен (Telegram WebView / Safari Private).
            // Если в storage что-то есть и rehydration вернёт admin — будет повторный
            // ререндер с авторизованным состоянием; кратковременная "вспышка" формы
            // допустима и лучше, чем бесконечный спиннер.
            isReady: true,

            isExpired: () => {
                const savedAt = get().savedAt;
                if (!savedAt) return true;
                return Date.now() - savedAt > TTL_MS;
            },
        }),
        {
            name: STORAGE_KEY,
            storage: createJSONStorage(() => ttlStorage),
            partialize: (state) => ({
                env: state.env,
                admin: state.admin,
                locationId: state.locationId,
                adminBotId: state.adminBotId,
                clientBotId: state.clientBotId,
                adminUserIdLong: state.adminUserIdLong,
                savedAt: state.savedAt,
            }),
            onRehydrateStorage: () => (state) => {
                if (!state) return;

                state.isReady = true;

                if (state.isExpired()) {
                    state.clear();
                }
            },
        }
    )
);
